/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * ### Corpus Header
 * ![header](../images/frontend/manage-the-bot/corpus-header.png)
 *  @function
 * @memberOf ManageTheBot
 * @name Corpus
 */

import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MESSAGES } from '@translations/labels';
import {
  useCorpusVersionGet,
  useCorpusVersionDelete,
  useCorpusVersionLoading,
  useCorpusVersionCreation,
  useCorpusVersionSelection,
  useValidateCorpusVersion,
} from 'commons/hooks/useCorpusVersion';
import { Select, message, Form, Upload, Button, Tag, Spin } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { idField, baseURL } from '@constants';
import { formatDateTimeNoSeconds } from '@utils/format/dates';
import { useAgentId } from '@hooks/useAgentId';
import { isEmpty } from '@utils/validation/isEmpty';
import { AppContext } from 'commons/contexts/AppContext';
import { errorAlert } from '@store/reducers/alerts';
import { handleImport, handleExport } from '../common/corpusHelper';
import { useDomainsSearch } from '../domains/useDomainsSearch';
import { useIntentsSearch } from '../hooks/useIntentsSearch';
import { openSaveVersionModal } from './components/saveVersionModal';
import { openConfirmDeleteModal } from './components/deleteVersionModal';
import EvaluateCorpusModal from './components/evaluateCorpusModal';
import CorpusMenu from './components/corpusMenu';
import corpusHeaderStyles from './corpusHeaderStyles';
import DuplicatedIntentsModal from './components/duplicatedIntentsModal';
import { getUserRole } from '@utils/authentication';

const labels = { ...MESSAGES.manageBot };

const CorpusHeader = () => {
  const { releases } = useCorpusVersionGet();
  const { loadingReleases } = useCorpusVersionLoading();
  const { selectedCorpusId, setSelectedCorpusId, setShowImportErrors, setDomainToFilterIntents, axaConfig } =
    useContext(AppContext);
  const { agentId } = useAgentId();
  const { createCorpusVersion } = useCorpusVersionCreation();
  const { deleteVersion } = useCorpusVersionDelete();
  const [currentTitle, setVersionTitle] = useState();
  const [currentDescription, setVersionDescription] = useState();
  const { selectVersion } = useCorpusVersionSelection();
  const { searchDomains } = useDomainsSearch(selectedCorpusId);
  const { searchIntents } = useIntentsSearch();
  const intents = useSelector(state => state.intents.all);
  const domains = useSelector(state => state.domains.all);
  const dispatch = useDispatch();
  const trainedVersionId = useSelector(state => state.agents?.updates?.training?.versionId);
  const { validateCorpusVersion } = useValidateCorpusVersion();

  const changeVersion = value => {
    const versionId = isEmpty(value) ? undefined : value;
    searchDomains(agentId, versionId);
    searchIntents(agentId, versionId);
    setSelectedCorpusId(versionId);
    console.log('changeVersion -> versionId', versionId);
    if (versionId) {
      const release = releases.find(r => r[idField] === versionId);
      setVersionTitle(release.releaseTitle || 'no name');
      setVersionDescription(release.releaseDescription || 'no description');
      selectVersion({
        versionId,
        versionTitle: release.releaseTitle,
      });
    } else {
      setVersionTitle();
      setVersionDescription();
      selectVersion();
    }
  };

  useEffect(() => {
    if (releases && releases.length) {
      changeVersion(selectedCorpusId);
    } else {
      changeVersion();
    }
  }, [selectedCorpusId, releases]);

  const forkVersionClick = () => {
    validateCorpusVersion(selectedCorpusId);
    setSelectedCorpusId(undefined);
    selectVersion(undefined);

    setVersionDescription(`New version ready to edit!`);
  };

  const afterImportCallback = () => {
    setShowImportErrors(true);
    searchDomains(agentId, '');
    searchIntents(agentId, '');
  };

  const deleteVersionClick = () => {
    if (selectedCorpusId) {
      openConfirmDeleteModal(selectedCorpusId, setSelectedCorpusId, currentTitle, deleteVersion);
    } else {
      openConfirmDeleteModal('draft', setSelectedCorpusId, 'Corpus (work in progress)', deleteVersion);
    }
    console.log('deleteVersionClick > deleteVersionClick', deleteVersionClick);
  };

  // const evaluateCorpusClick = () => {
  //   if (selectedCorpusId) {
  //     evaluateCorpusModal(agentId, selectedCorpusId, setSelectedCorpusId, currentTitle);
  //   } else {
  //     evaluateCorpusModal(agentId, 'draft', setSelectedCorpusId, 'Corpus (work in progress)');
  //   }
  // };

  const showShowDeleteCorpusButton = () => {
    return Boolean(domains && domains.length);
  };

  return (
    <div className="corpus-header-container">
      <div className="corpus-header-title-buttons">
        <div>
          <div style={{ display: 'flex' }}>
            <div style={{ marginRight: '10px' }}>
              <h3 style={{ marginBottom: '5px' }}>{currentTitle || MESSAGES.manageBot.corpus} </h3>
              {currentDescription && <h5 style={{ marginBottom: '30px' }}>{currentDescription}</h5>}
            </div>
            <div style={{ marginTop: '8px' }}>{!currentTitle && <Tag>Work in progress</Tag>}</div>
            <div style={{ marginTop: '8px' }}>{currentTitle && <Tag>Read only</Tag>}</div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {axaConfig?.enableEvaluate && Boolean(intents?.length) && (
              <EvaluateCorpusModal selectedCorpusId={selectedCorpusId} />
            )}
            {!selectedCorpusId && (
              <>
                {showShowDeleteCorpusButton() && (
                  <Button
                    style={{ marginRight: '1rem' }}
                    type="secondary"
                    className="uppercase custom-button-primary "
                    onClick={() => deleteVersionClick()}
                  >
                    Clear corpus
                  </Button>
                )}
                <Upload {...handleImport(baseURL, agentId, message, afterImportCallback)}>
                  <Button type="secondary" className="custom-button-primary uppercase">
                    <UploadOutlined className="main-button-icon-primary" />
                    {labels.releasesPage.btnImportCorpus}
                  </Button>
                </Upload>
              </>
            )}
            {selectedCorpusId && (
              <>
                <Form name="export-corpus" onFinish={handleExport(baseURL, agentId, selectedCorpusId)}>
                  <Button type="secondary" htmlType="submit" className="custom-button-primary uppercase">
                    <DownloadOutlined className="main-button-icon-primary" />
                    {labels.releasesPage.btnExportCorpus}
                  </Button>
                </Form>
                <Button
                  style={{ marginLeft: '1rem' }}
                  type="secondary"
                  className="uppercase custom-button-primary "
                  onClick={() => deleteVersionClick(selectedCorpusId)}
                >
                  <DeleteOutlined className="main-button-icon-primary" />
                  Delete version
                </Button>
                {getUserRole() !== 'viewer' && (
                  <Button
                    style={{ marginLeft: '1rem' }}
                    type="secondary"
                    className="uppercase custom-button-primary "
                    onClick={forkVersionClick}
                  >
                    <CopyOutlined />
                    {labels.btnforkIntentsVersion}
                  </Button>
                )}
              </>
            )}
            {intents && intents.length > 0 && !selectedCorpusId && (
              <Button
                type="primary"
                className="uppercase"
                style={{ marginLeft: '1rem' }}
                onClick={() => {
                  if (trainedVersionId) {
                    dispatch(errorAlert('You need to train the bot before saving a version'));
                  } else {
                    openSaveVersionModal(createCorpusVersion);
                  }
                }}
              >
                {labels.btnSaveCorpusVersion}
              </Button>
            )}
          </div>

          <div style={{ marginTop: '1rem' }}>
            <>
              {loadingReleases && (
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <span style={{ marginRight: '10px' }}>Corpus versions...</span> <Spin />
                </div>
              )}
              {!!releases && !!releases.length && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Select
                    allowClear
                    placeholder="-- Select corpus version --"
                    onChange={e => {
                      setSelectedCorpusId(e);
                      setDomainToFilterIntents(null);
                    }}
                    value={selectedCorpusId}
                    style={{ width: 400, marginLeft: '5px' }}
                  >
                    {releases.map(releaseItem => (
                      <Select.Option key={releaseItem[idField]} value={releaseItem[idField]}>
                        {releaseItem.releaseTitle || releaseItem.releaseDescription}{' '}
                        {releaseItem.releaseDate ? `(${formatDateTimeNoSeconds(releaseItem.releaseDate)}h)` : ''}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </>
          </div>
        </div>
      </div>
      <CorpusMenu />
      <DuplicatedIntentsModal />
      <style jsx>{corpusHeaderStyles}</style>
    </div>
  );
};

export { CorpusHeader };
